import React, { useEffect, useMemo, useRef, useState } from "react";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { ChevronDown } from "lucide-react";

interface MarkdownAccordionProps {
  source: string;
  style?: React.CSSProperties;
}

interface Section {
  title: string;
  content: string;
  level: number;
}

type ParsedContent =
  | {
      type: "accordion";
      introContent?: string;
      sections: Section[];
      outroContent?: string;
    }
  | {
      type: "headings";
      sections: Section[];
    };

/**
 * Remove incomplete accordion tags during streaming to prevent React warnings
 * Only keeps complete <accordion>...</accordion> pairs
 */
const stripIncompleteAccordions = (markdown: string): string => {
  // Find all complete accordion pairs
  const completeRegex =
    /<accordion\s+title="([^"]+)"\s*>([\s\S]*?)<\/accordion>/gi;
  const completeMatches: Array<{ start: number; end: number }> = [];
  let match;

  while ((match = completeRegex.exec(markdown)) !== null) {
    completeMatches.push({
      start: match.index,
      end: match.index + match[0].length,
    });
  }

  // Find all opening accordion tags (including incomplete ones)
  const openingRegex = /<accordion(?:\s+title="[^"]*")?[^>]*>/gi;
  const partsToKeep: string[] = [];
  let lastIndex = 0;

  while ((match = openingRegex.exec(markdown)) !== null) {
    const openingStart = match.index;
    const openingEnd = match.index + match[0].length;

    // Check if this opening tag is part of a complete pair
    const isComplete = completeMatches.some(
      (complete) => complete.start === openingStart,
    );

    if (isComplete) {
      // Keep everything up to this opening tag
      partsToKeep.push(markdown.slice(lastIndex, openingStart));
      // Find the matching complete pair and keep it
      const completePair = completeMatches.find(
        (c) => c.start === openingStart,
      );
      if (completePair) {
        partsToKeep.push(markdown.slice(completePair.start, completePair.end));
        lastIndex = completePair.end;
      }
    } else {
      // Incomplete opening tag - keep everything before it, skip the tag
      partsToKeep.push(markdown.slice(lastIndex, openingStart));
      lastIndex = openingEnd;
    }
  }

  // Add remaining content after last processed position
  partsToKeep.push(markdown.slice(lastIndex));

  return partsToKeep.join("");
};

const extractAccordionSections = (markdown: string): ParsedContent | null => {
  // Strip incomplete accordions first to prevent React warnings during streaming
  const cleanedMarkdown = stripIncompleteAccordions(markdown);

  const accordionRegex =
    /<accordion\s+title="([^"]+)"\s*>([\s\S]*?)<\/accordion>/gi;
  const sections: Section[] = [];
  let match: RegExpExecArray | null;
  let cursor = 0;
  let introContent = "";

  while ((match = accordionRegex.exec(cleanedMarkdown)) !== null) {
    const [fullMatch, rawTitle, rawContent] = match;
    const startIndex = match.index;

    if (sections.length === 0 && startIndex > 0) {
      introContent = cleanedMarkdown.slice(0, startIndex).trim();
    }

    sections.push({
      title: rawTitle.trim(),
      content: rawContent.trim(),
      level: 2,
    });

    cursor = startIndex + fullMatch.length;
  }

  if (sections.length === 0) {
    return null;
  }

  const outroContent = cleanedMarkdown.slice(cursor).trim();

  return {
    type: "accordion",
    introContent: introContent || undefined,
    sections,
    outroContent: outroContent || undefined,
  };
};

const normalizeAccordionMarkup = (markdown: string): string => {
  // First strip incomplete accordions to prevent warnings
  const cleaned = stripIncompleteAccordions(markdown);
  return cleaned
    .replace(
      /<accordion\s+title="([^"]+)"\s*>/gi,
      (_, title) => `\n## ${title.trim()}\n`,
    )
    .replace(/<\/accordion>/gi, "\n");
};

const parseHeadingSections = (markdown: string): ParsedContent => {
  const normalized = normalizeAccordionMarkup(markdown);
  const lines = normalized.split("\n");
  const sections: Section[] = [];
  let currentSection: Section | null = null;
  let beforeFirstHeading: string[] = [];

  for (const line of lines) {
    const headingMatch = line.match(/^(#{2,})\s+(.+)$/);

    if (headingMatch) {
      if (currentSection) {
        sections.push(currentSection);
      }

      if (beforeFirstHeading.length > 0 && sections.length === 0) {
        sections.push({
          title: "Introduction",
          content: beforeFirstHeading.join("\n").trim(),
          level: 0,
        });
        beforeFirstHeading = [];
      }

      const level = headingMatch[1].length;
      const title = headingMatch[2].trim();
      currentSection = {
        title,
        content: "",
        level,
      };
    } else {
      if (currentSection) {
        currentSection.content += line + "\n";
      } else {
        beforeFirstHeading.push(line);
      }
    }
  }

  if (currentSection) {
    sections.push(currentSection);
  }

  if (sections.length === 0 && beforeFirstHeading.length > 0) {
    sections.push({
      title: "Content",
      content: beforeFirstHeading.join("\n").trim(),
      level: 0,
    });
  }

  return { type: "headings", sections };
};

const parseMarkdownSections = (markdown: string): ParsedContent => {
  return extractAccordionSections(markdown) ?? parseHeadingSections(markdown);
};

const ensureBulletLineBreaks = (markdown: string): string =>
  markdown?.replace(/\n•\s+/g, "\n\n• ") || "";

const MarkdownAccordion: React.FC<MarkdownAccordionProps> = ({
  source,
  style,
}) => {
  const normalizedSource = useMemo(
    () => ensureBulletLineBreaks(source || ""),
    [source],
  );
  const parsed = useMemo(
    () => parseMarkdownSections(normalizedSource),
    [normalizedSource],
  );

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const [openIndex, setOpenIndex] = useState<number>(-1);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Calculate these values early for hooks
  const introContent =
    parsed.type === "accordion"
      ? parsed.introContent
      : parsed.sections.find((s) => s.level === 0)?.content;

  const accordionSections =
    parsed.type === "accordion"
      ? parsed.sections
      : parsed.sections.filter((s) => s.level > 0);

  const trailingContent =
    parsed.type === "accordion" ? parsed.outroContent : undefined;

  const sectionCount = accordionSections.length;

  // ALL useEffect hooks must be called unconditionally
  useEffect(() => {
    setOpenIndex(-1);
  }, [source, sectionCount]);

  useEffect(() => {
    if (openIndex < 0) return;
    const target = buttonRefs.current[openIndex];
    target?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [openIndex]);

  // NOW we can have conditional returns after all hooks are called
  if (parsed.type === "headings" && parsed.sections.length <= 1) {
    return <MarkdownPreview source={normalizedSource} style={style} />;
  }

  if (accordionSections.length === 0) {
    return <MarkdownPreview source={normalizedSource} style={style} />;
  }

  const toggleSection = (index: number) => {
    setOpenIndex((prev) => (prev === index ? -1 : index));
  };

  return (
    <div style={style} className="space-y-4 text-[#1D1B19] dark:text-[#E3E6EA]">
      {introContent && (
        <div className="rounded-xl px-3.5 py-3 text-[14px] leading-7 dark:text-[#E3E6EA]">
          <MarkdownPreview
            source={introContent}
            style={{
              ...style,
              padding: 0,
              backgroundColor: "transparent",
            }}
          />
        </div>
      )}

      <div className="space-y-3 rounded-md p-1">
        {accordionSections.map((section, index) => {
          const isOpen = openIndex === index;
          const panelId = `markdown-accordion-panel-${index}`;

          return (
            <div
              key={section.title + index}
              className="overflow-hidden rounded-xl border border-[#E5DDD3] dark:border-[#455D7D] bg-[#F3ECE4] dark:bg-[#2C405F] shadow-sm"
            >
              <button
                type="button"
                ref={(element) => {
                  buttonRefs.current[index] = element;
                }}
                className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-[14px] font-semibold leading-[125%] text-[#1D1B19] dark:text-[#F3F4F6] transition hover:bg-[#F0E4D9] dark:hover:bg-[#455D7D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2C405F] dark:focus-visible:outline-[#6E829E] ${
                  isOpen ? "rounded-b-none" : ""
                }`}
                onClick={() => toggleSection(index)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                aria-label={`Toggle ${section.title}`}
              >
                <span className="flex-1">{section.title}</span>
                <ChevronDown
                  size={18}
                  className={`text-[#8E8378] dark:text-[#9AAABF] transition-transform duration-200 ${
                    isOpen ? "rotate-180" : "rotate-0"
                  }`}
                />
              </button>
              <div
                id={panelId}
                aria-hidden={!isOpen}
                className={`overflow-hidden transition-[max-height] duration-300 ease-out ${
                  isOpen ? "max-h-none" : "max-h-0"
                }`}
              >
                <div className="markdown-accordion-body border-t border-transparent px-3 pb-4 pt-2 overflow-x-auto dark:text-[#E3E6EA]">
                  <MarkdownPreview
                    source={section.content.trim()}
                    style={{
                      ...style,
                      padding: 0,
                      backgroundColor: "transparent",
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {trailingContent && (
        <div className="rounded-xl px-3.5 py-3 dark:text-[#E3E6EA]">
          <MarkdownPreview
            source={trailingContent}
            style={{
              ...style,
              padding: 0,
              backgroundColor: "transparent",
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MarkdownAccordion;
